import { forwardRef, useImperativeHandle, useState, useCallback, useRef, useEffect } from 'react';
import { COUNTRY_CODES, COUNTRIES, validatePhoneNumber, getPhoneValidationRules } from '../../constants/countries.js';

/**
 * ConsumerRegistrationForm
 * Form component for creating/editing consumer accounts
 * Features: Two-step form, validation, edit password mode, country code selection
 */
const ConsumerRegistrationForm = forwardRef(({ onSubmit, initialData, isLoading }, ref) => {
  const [step, setStep] = useState(initialData ? 2 : 1); // Skip step 1 if editing
  
  // Extract phone and country code from initialData if available
  const parsePhone = (phone) => {
    if (!phone) return { countryCode: '+91', phoneNumber: '' };
    const match = phone.match(/^(\+\d{1,3})(.*)$/);
    if (match) {
      return { countryCode: match[1], phoneNumber: match[2].trim() };
    }
    return { countryCode: '+91', phoneNumber: phone };
  };

  const initialPhone = parsePhone(initialData?.profile_consumer?.phone);

  const [formData, setFormData] = useState({
    email: initialData?.email || '',
    name: initialData?.name || '',
    password: initialData?.password ? '••••••••' : '', // Show masked password in edit mode
    password_confirmation: '',
    phoneCountryCode: initialPhone.countryCode,
    phoneNumber: initialPhone.phoneNumber,
    gender: initialData?.profile_consumer?.gender || '',
    country: initialData?.profile_consumer?.country || '',
    location: initialData?.profile_consumer?.location || '',
    bio: initialData?.profile_consumer?.bio || '',
    language: initialData?.profile_consumer?.language || 'en',
    timezone: initialData?.profile_consumer?.timezone || 'UTC',
    notifications_email: initialData?.profile_consumer?.notification_email ?? true,
    notifications_sms: initialData?.profile_consumer?.notification_sms ?? false,
    notifications_push: initialData?.profile_consumer?.notification_push ?? true,
  });

  const [countryFilter, setCountryFilter] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const [errors, setErrors] = useState({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [editPasswordMode, setEditPasswordMode] = useState(false);
  const originalPassword = useRef(initialData?.password ? '••••••••' : '');

  /**
   * Validate form fields
   */
  const validate = useCallback(() => {
    const newErrors = {};

    // Step 1 validation
    if (step === 1) {
      if (!formData.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
      } else if (formData.name.trim().length < 2) {
        newErrors.name = 'Name must be at least 2 characters';
      }

      if (!formData.password?.trim()) {
        if (!initialData) {
          newErrors.password = 'Password is required';
        } else if (initialData && editPasswordMode) {
          newErrors.password = 'Password is required';
        }
      } else if (formData.password !== '••••••••' && formData.password.length < 8) {
        // Only validate length if not the masked password
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (initialData && editPasswordMode) {
        // When editing password, both fields are required
        if (!formData.password_confirmation?.trim()) {
          newErrors.password_confirmation = 'Please confirm your password';
        } else if (formData.password !== formData.password_confirmation) {
          newErrors.password_confirmation = 'Passwords do not match';
        }
      } else if (!initialData) {
        // When creating new consumer
        if (!formData.password_confirmation?.trim()) {
          newErrors.password_confirmation = 'Please confirm your password';
        } else if (formData.password !== formData.password_confirmation) {
          newErrors.password_confirmation = 'Passwords do not match';
        }
      }
    }

    // Step 2 validation
    if (step === 2) {
      // Phone validation (if provided) - with country-specific rules
      if (formData.phoneNumber?.trim()) {
        const phoneError = validatePhoneNumber(formData.phoneNumber, formData.phoneCountryCode);
        if (phoneError) {
          newErrors.phoneNumber = phoneError;
        }
      }

      // Gender validation (if provided)
      if (formData.gender && !['male', 'female', 'other', 'prefer_not_to_say'].includes(formData.gender)) {
        newErrors.gender = 'Please select a valid gender';
      }

      // Country validation (if provided)
      if (formData.country?.trim()) {
        if (!COUNTRIES.includes(formData.country)) {
          newErrors.country = 'Please select a valid country from the list';
        }
      }

      // Location validation (if provided)
      if (formData.location?.trim()) {
        if (formData.location.trim().length < 2) {
          newErrors.location = 'Location must be at least 2 characters';
        }
        if (formData.location.trim().length > 100) {
          newErrors.location = 'Location must not exceed 100 characters';
        }
      }

      // Bio validation (if provided)
      if (formData.bio?.trim()) {
        if (formData.bio.trim().length < 5) {
          newErrors.bio = 'Bio must be at least 5 characters';
        }
        if (formData.bio.trim().length > 500) {
          newErrors.bio = 'Bio must not exceed 500 characters';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [step, formData]);

  /**
   * Handle field change
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  /**
   * Handle phone number change - only allows digits
   */
  const handlePhoneChange = (e) => {
    const { value } = e.target;
    // Extract only digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // Get max digits for current country code
    const rules = getPhoneValidationRules(formData.phoneCountryCode);
    
    // Only allow up to maxDigits
    const limited = digitsOnly.slice(0, rules.maxDigits);
    
    setFormData((prev) => ({
      ...prev,
      phoneNumber: limited,
    }));
    
    // Clear error for phone field
    if (errors.phoneNumber) {
      setErrors((prev) => ({
        ...prev,
        phoneNumber: '',
      }));
    }
  };

  /**
   * Handle country selection
   */
  const handleCountrySelect = (country) => {
    setFormData((prev) => ({
      ...prev,
      country: country,
    }));
    if (errors.country) {
      setErrors((prev) => ({
        ...prev,
        country: '',
      }));
    }
    setCountryFilter('');
    setShowCountryDropdown(false);
  };

  /**
   * Filter countries based on search
   */
  const filteredCountries = countryFilter
    ? COUNTRIES.filter((c) =>
        c.toLowerCase().startsWith(countryFilter.toLowerCase())
      ).slice(0, 10)
    : COUNTRIES.slice(0, 10);

  /**
   * Handle cancel edit password
   */
  const handleCancelEditPassword = () => {
    setEditPasswordMode(false);
    setFormData((prev) => ({
      ...prev,
      password: originalPassword.current,
      password_confirmation: '',
    }));
    setErrors((prev) => ({
      ...prev,
      password: '',
      password_confirmation: '',
    }));
    setPasswordVisible(false);
    setConfirmPasswordVisible(false);
  };

  /**
   * Handle next step
   */
  const handleNext = () => {
    if (validate()) {
      setStep(2);
    }
  };

  /**
   * Handle previous step
   */
  const handlePrevious = () => {
    setStep(1);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    // Build payload - strip unnecessary fields
    const payload = {
      email: formData.email.trim(),
      name: formData.name.trim(),
      // password_confirmation is NOT sent to backend - used only for frontend validation
      // Combine country code and phone number
      ...(formData.phoneNumber?.trim() && {
        phone: `${formData.phoneCountryCode}${formData.phoneNumber.replace(/\s/g, '')}`,
      }),
      ...(formData.gender && { gender: formData.gender }),
      ...(formData.country && { country: formData.country }),
      ...(formData.location?.trim() && { location: formData.location.trim() }),
      ...(formData.bio?.trim() && { bio: formData.bio.trim() }),
      language: formData.language,
      timezone: formData.timezone,
      notification_email: formData.notifications_email,
      notification_sms: formData.notifications_sms,
      notification_push: formData.notifications_push,
    };

    // Only include password if it's not the masked placeholder or if creating new user
    if (!initialData) {
      // Creating new user - password is required
      payload.password = formData.password;
    } else if (formData.password !== '••••••••' && editPasswordMode) {
      // Editing existing user and password was changed
      payload.password = formData.password;
    }
    // If editing and password is still masked or not in edit mode, don't include password

    try {
      await onSubmit(payload);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  /**
   * Expose methods via useImperativeHandle
   */
  useImperativeHandle(ref, () => ({
    submitForm: handleSubmit,
    validate: validate,
    getStep: () => step,
    setStep: (newStep) => setStep(newStep),
    reset: (data) => {
      const maskedPassword = data?.password ? '••••••••' : '';
      originalPassword.current = maskedPassword;
      const initialPhone = parsePhone(data?.profile_consumer?.phone);
      setFormData({
        email: data?.email || '',
        name: data?.name || '',
        password: maskedPassword,
        password_confirmation: '',
        phoneCountryCode: initialPhone.countryCode,
        phoneNumber: initialPhone.phoneNumber,
        gender: data?.profile_consumer?.gender || '',
        country: data?.profile_consumer?.country || '',
        location: data?.profile_consumer?.location || '',
        bio: data?.profile_consumer?.bio || '',
        language: data?.profile_consumer?.language || 'en',
        timezone: data?.profile_consumer?.timezone || 'UTC',
        notifications_email: data?.profile_consumer?.notification_email ?? true,
        notifications_sms: data?.profile_consumer?.notification_sms ?? false,
        notifications_push: data?.profile_consumer?.notification_push ?? true,
      });
      setErrors({});
      setPasswordVisible(false);
      setConfirmPasswordVisible(false);
      setEditPasswordMode(false);
      setCountryFilter('');
      setShowCountryDropdown(false);
      setStep(data ? 2 : 1);
    },
  }));

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        <div
          className={`flex-1 h-1 rounded-full ${
            step >= 1 ? 'bg-blue-600' : 'bg-slate-200'
          } transition-colors`}
        />
        <div
          className={`flex-1 h-1 rounded-full ${
            step >= 2 ? 'bg-blue-600' : 'bg-slate-200'
          } transition-colors`}
        />
      </div>

      {/* Step 1: Account Details */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading || !!initialData}
              placeholder="rajesh.kumar@example.com"
              className={`w-full px-3 py-2 text-[13px] border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                initialData ? 'bg-slate-100 cursor-not-allowed' : ''
              } ${
                errors.email
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
            />
            {errors.email && (
              <p className="text-[11px] text-red-600 mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="Rajesh Kumar"
              className={`w-full px-3 py-2 text-[13px] border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                errors.name
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
            />
            {errors.name && (
              <p className="text-[11px] text-red-600 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Password Section */}
          <div className="border-t border-slate-200 pt-4 space-y-4">
            {/* Password Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[12px] font-semibold text-slate-700">
                  Password {!initialData && <span className="text-red-500">*</span>}
                </label>
                {/* Edit Password / Cancel button - toggle in place */}
                {initialData && !editPasswordMode && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditPasswordMode(true);
                      setFormData((prev) => ({ ...prev, password: '', password_confirmation: '' }));
                      setPasswordVisible(false);
                    }}
                    disabled={isLoading}
                    className="text-[11px] text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Edit Password
                  </button>
                )}
                {initialData && editPasswordMode && (
                  <button
                    type="button"
                    onClick={handleCancelEditPassword}
                    disabled={isLoading}
                    className="text-[11px] text-slate-600 hover:text-slate-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading || (initialData && !editPasswordMode)}
                  placeholder={
                    initialData && !editPasswordMode
                      ? '••••••••'
                      : initialData && editPasswordMode
                      ? 'Enter new password'
                      : 'Minimum 8 characters'
                  }
                  className={`w-full px-3 py-2 pr-10 text-[13px] border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                    initialData && !editPasswordMode ? 'bg-slate-100 cursor-not-allowed' : ''
                  } ${
                    errors.password
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                />
                {/* Eye icon - only show when there's a real password to toggle */}
                {(!initialData || editPasswordMode) && formData.password.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    disabled={isLoading}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
                    title={passwordVisible ? 'Hide password' : 'Show password'}
                  >
                    {passwordVisible ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-4.803m5.596-3.856A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
              {/* Password Encrypted Indicator */}
              {initialData && !editPasswordMode && (
                <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5s-5 2.24-5 5v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="text-[11px] font-medium text-blue-900">Password is encrypted</span>
                    <span className="text-[10px] text-blue-700">Click "Edit Password" to change</span>
                  </div>
                </div>
              )}
              {errors.password && (
                <p className="text-[11px] text-red-600 mt-1">{errors.password}</p>
              )}
            </div>

            {/* Re-enter Password - Show when creating new OR when editing and editPasswordMode is true */}
            {(!initialData || editPasswordMode) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[12px] font-semibold text-slate-700">
                    Re-enter Password <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="relative">
                  <input
                    type={confirmPasswordVisible ? 'text' : 'password'}
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    disabled={isLoading}
                    placeholder="Confirm your password"
                    className={`w-full px-3 py-2 pr-10 text-[13px] border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                      errors.password_confirmation
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                  {formData.password_confirmation.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                      disabled={isLoading}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
                      title={confirmPasswordVisible ? 'Hide password' : 'Show password'}
                    >
                      {confirmPasswordVisible ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-4.803m5.596-3.856A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411M3 3l18 18" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
                {errors.password_confirmation && (
                  <p className="text-[11px] text-red-600 mt-1">{errors.password_confirmation}</p>
                )}
              </div>
            )}
          </div>

          {/* Navigation Buttons - Moved to drawer footer */}
        </div>
      )}

      {/* Step 2: Profile & Preferences */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Phone with Country Code */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-2">
              Phone Number
            </label>
            <div className="flex gap-2">
              <select
                name="phoneCountryCode"
                value={formData.phoneCountryCode}
                onChange={handleChange}
                disabled={isLoading}
                className="px-3 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
              >
                {COUNTRY_CODES.map((cc, idx) => (
                  <option key={`${cc.code}-${idx}`} value={cc.code}>
                    {cc.code} {cc.countryName}
                  </option>
                ))}
              </select>
              <div className="flex-1">
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  disabled={isLoading}
                  placeholder={getPhoneValidationRules(formData.phoneCountryCode).example}
                  className={`w-full px-3 py-2 text-[13px] border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                    errors.phoneNumber
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                />
              </div>
            </div>
            {errors.phoneNumber && (
              <p className="text-[11px] text-red-600 mt-1">{errors.phoneNumber}</p>
            )}
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-slate-500">
                Format: {formData.phoneCountryCode} {formData.phoneNumber || getPhoneValidationRules(formData.phoneCountryCode).example}
              </p>
              <p className="text-[10px] text-slate-500">
                {formData.phoneNumber.length} / {getPhoneValidationRules(formData.phoneCountryCode).maxDigits} digits
              </p>
            </div>
          </div>

          {/* Gender & Country */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                Gender
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                disabled={isLoading}
                className="w-full px-3 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
              {errors.gender && (
                <p className="text-[11px] text-red-600 mt-1">{errors.gender}</p>
              )}
            </div>

            {/* Country with Dropdown & Search */}
            <div className="relative">
              <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                Country
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={showCountryDropdown ? countryFilter : formData.country}
                  onChange={(e) => {
                    setCountryFilter(e.target.value);
                    if (!showCountryDropdown) {
                      setShowCountryDropdown(true);
                    }
                  }}
                  onFocus={() => {
                    setShowCountryDropdown(true);
                    setCountryFilter('');
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowCountryDropdown(false), 200);
                  }}
                  disabled={isLoading}
                  placeholder="Search country..."
                  className={`w-full px-3 py-2 text-[13px] border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                    errors.country
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                />

                {/* Dropdown */}
                {showCountryDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country) => (
                        <button
                          key={country}
                          type="button"
                          onClick={() => handleCountrySelect(country)}
                          className="w-full text-left px-3 py-2 text-[13px] hover:bg-blue-50 transition-colors text-slate-700"
                        >
                          {country}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-[12px] text-slate-500">
                        No countries found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {errors.country && (
                <p className="text-[11px] text-red-600 mt-1">{errors.country}</p>
              )}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-2">
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="Mumbai, Maharashtra"
              className={`w-full px-3 py-2 text-[13px] border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                errors.location
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
            />
            {errors.location && (
              <p className="text-[11px] text-red-600 mt-1">{errors.location}</p>
            )}
            <p className="text-[10px] text-slate-500 mt-1">City, State/Region</p>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-2">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="Software engineer from Bangalore interested in technology and cricket..."
              rows="3"
              maxLength="500"
              className={`w-full px-3 py-2 text-[13px] border rounded-lg focus:outline-none focus:ring-1 transition-colors resize-none ${
                errors.bio
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
            />
            {errors.bio && (
              <p className="text-[11px] text-red-600 mt-1">{errors.bio}</p>
            )}
            <p className="text-[10px] text-slate-500 mt-1">
              {formData.bio.length}/500 characters
            </p>
          </div>
        </div>
      )}
    </div>
  );
});

ConsumerRegistrationForm.displayName = 'ConsumerRegistrationForm';

export default ConsumerRegistrationForm;
